import React, { FormEvent, KeyboardEvent, useEffect, useRef, useState } from 'react'

import Fuse from 'fuse.js'
import { AlertCircle, CheckCircle, Loader2, Sparkles } from 'lucide-react'

import ChainToken from '@src/components/ChainToken'
import { ChainId } from '@src/configs/chainInfo'
import { getChainNameFromChainId } from '@src/configs/chainInfo'
import { useExchangeDataContext } from '@src/pages/Exchange/context/ExchangeDataContext'
import { useExchangeState } from '@src/pages/Exchange/context/ExchangeStateContext'
import { useConfigStore, useTokensStore } from '@src/zustand_stores'

interface AIInputBoxProps {
    className?: string
    setMax?: (percentageAmount?: number) => void
}

const AIInputBox: React.FC<AIInputBoxProps> = ({ className = '', setMax }) => {
    const [prompt, setPrompt] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [loadingStage, setLoadingStage] = useState<'processing' | 'analyzing' | 'finishing' | null>(null)
    const [requestProgress, setRequestProgress] = useState(0) // Track actual API request progress
    const [error, setError] = useState<string | null>(null)
    const [success, setSuccess] = useState<string | null>(null)
    const [suggestions, setSuggestions] = useState<
        Array<{
            type: 'token' | 'chain'
            value: string
            display: string
            data: any
            shortcutNumber: number
            uniqueId: string
            isChainMatch?: boolean
        }>
    >([])
    const [activeSuggestionIndex, setActiveSuggestionIndex] = useState<number>(-1)
    const [showSuggestions, setShowSuggestions] = useState<boolean>(false)
    const [cursorPosition, setCursorPosition] = useState<{ top: number; left: number; inputWidth: number }>({
        top: 0,
        left: 0,
        inputWidth: 0,
    })
    const [previewText, setPreviewText] = useState<string | null>(null)
    const [hoveredIndex, setHoveredIndex] = useState<number>(-1)
    const [undoHistory, setUndoHistory] = useState<string[]>([])
    const [inlinePreviewText, setInlinePreviewText] = useState<string>('')
    const inputRef = useRef<HTMLInputElement>(null)
    const suggestionsRef = useRef<HTMLDivElement>(null)

    const { updateSellData, updateBuyData, refetchQuote } = useExchangeDataContext()
    const { setSellAmountString } = useExchangeState()
    const tokens = useTokensStore(state => state.tokens)
    const availableNetworks = useConfigStore(config => config.availableNetworks)

    // Update loading stages based on real request progress
    useEffect(() => {
        if (isLoading) {
            if (requestProgress < 30) {
                setLoadingStage('processing')
            } else if (requestProgress < 70) {
                setLoadingStage('analyzing')
            } else {
                setLoadingStage('finishing')
            }
        } else {
            setLoadingStage(null)
            setRequestProgress(0)
        }
    }, [isLoading, requestProgress])

    // Effect to animate the progress bar when loading
    useEffect(() => {
        let progressInterval: NodeJS.Timeout

        if (isLoading && requestProgress < 90) {
            // Gradually increase progress to simulate activity
            progressInterval = setInterval(() => {
                setRequestProgress(prev => {
                    // Different speeds at different stages
                    if (prev < 30) return Math.min(prev + 2, 30)
                    if (prev < 60) return Math.min(prev + 1, 60)
                    if (prev < 85) return Math.min(prev + 0.5, 85)
                    return Math.min(prev + 0.2, 90) // Slow down as we approach 90%
                })
            }, 100)
        }

        return () => {
            if (progressInterval) clearInterval(progressInterval)
        }
    }, [isLoading, requestProgress])

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault()

        if (!prompt.trim() || isLoading) return

        setIsLoading(true)
        setError(null)
        setSuccess(null)
        setRequestProgress(5) // Start at 5% for immediate feedback

        // Determine if we're in a local environment
        const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'

        // Only add the secret header when in a local environment
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
        }

        // Add shared secret only for local development
        if (isLocal) {
            headers['X-Shared-Secret'] = 'jYL2Eq8QYfUTufP2k9yD' // Make sure this matches env.SHARED_SECRET
        }

        // Create a request payload
        const payload = JSON.stringify({ prompt })

        // Use fetch for the request with cleaner error handling
        fetch('https://worker1.mach.exchange/parse', {
            method: 'POST',
            headers,
            body: payload,
        })
            .then(response => {
                // Set to near completion
                setRequestProgress(92)

                if (!response.ok) {
                    throw new Error(`Error ${response.status}: ${response.statusText}`)
                }
                return response.json()
            })
            .then(data => {
                // Move to 95%
                setRequestProgress(95)

                // Process the response data
                if (data.error) {
                    let errorMessage: string
                    if (typeof data.error === 'string') {
                        errorMessage = data.error
                    } else if (data.error instanceof Error) {
                        errorMessage = data.error.message
                    } else if (data.error && typeof data.error === 'object' && 'message' in data.error) {
                        errorMessage = String(data.error.message)
                    } else {
                        errorMessage = 'Unknown error occurred'
                    }
                    throw new Error(errorMessage)
                }

                // Check if we have the success wrapper structure
                const responseData = data.success && data.data ? data.data : data

                // Process the data similar to the existing implementation
                const sellToken = responseData.sellToken
                const buyToken = responseData.buyToken

                // Process tokens and update as before...
                let foundSell = false
                let foundBuy = false
                let useMaxAmount = false

                // Move to 97%
                setRequestProgress(97)

                // Process the sell token if present
                if (sellToken?.symbol) {
                    // Check if useMaxAmount is set
                    if (sellToken.useMaxAmount === true) {
                        useMaxAmount = true
                        // Maximum balance will be used
                    }

                    // Find token logic remains the same...
                    let token = null

                    if (sellToken.address) {
                        token = tokens.find(
                            t =>
                                t.address.toLowerCase() === sellToken.address?.toLowerCase() &&
                                (t.chainId === sellToken.chainId || !sellToken.chainId)
                        )
                    }

                    if (!token) {
                        token = tokens.find(
                            t =>
                                t.symbol.toLowerCase() === sellToken.symbol?.toLowerCase() &&
                                (!sellToken.chainId || t.chainId === sellToken.chainId)
                        )
                    }

                    // Set progress to indicate we're processing token data
                    setRequestProgress(98)

                    // Update sell data if token found
                    if (token) {
                        const updateData: {
                            token: typeof token
                            chainId: number
                            value?: bigint
                        } = {
                            token,
                            chainId: token.chainId || sellToken.chainId || ChainId.Ethereum,
                        }

                        // Handle amount logic as before...
                        if (sellToken.amount && !useMaxAmount) {
                            try {
                                const amountStr = String(sellToken.amount)
                                updateData.value = BigInt(amountStr)

                                // Format display amount
                                const decimals = token.decimals || 18
                                let displayAmount: string

                                if (amountStr.length <= decimals) {
                                    const padded = amountStr.padStart(decimals, '0')
                                    displayAmount = `0.${padded.slice(0, Math.min(8, decimals))}`
                                    displayAmount = displayAmount.replace(/\.?0+$/, '')
                                } else {
                                    const wholePart = amountStr.slice(0, amountStr.length - decimals)
                                    const fractionalPart = amountStr.slice(amountStr.length - decimals)
                                    displayAmount = `${wholePart}.${fractionalPart.slice(0, Math.min(8, decimals))}`
                                    displayAmount = displayAmount.replace(/\.?0+$/, '')
                                }

                                // Set display amount string
                                setSellAmountString(displayAmount)
                            } catch (error) {
                                console.error('Error converting amount:', error)
                            }
                        }

                        updateSellData(updateData)
                        foundSell = true
                    }
                }

                // Process buy token
                if (buyToken?.symbol) {
                    let token = null

                    if (buyToken.address) {
                        token = tokens.find(
                            t =>
                                t.address.toLowerCase() === buyToken.address?.toLowerCase() &&
                                (t.chainId === buyToken.chainId || !buyToken.chainId)
                        )
                    }

                    if (!token) {
                        token = tokens.find(
                            t =>
                                t.symbol.toLowerCase() === buyToken.symbol?.toLowerCase() &&
                                (!buyToken.chainId || t.chainId === buyToken.chainId)
                        )
                    }

                    if (token) {
                        const updateData = {
                            token,
                            chainId: token.chainId || buyToken.chainId || ChainId.Ethereum,
                        }

                        updateBuyData(updateData)
                        foundBuy = true
                    }
                }

                // Final processing and updating UI
                setTimeout(() => {
                    setRequestProgress(100)
                    handleSuccessfulResponse(foundSell, foundBuy, sellToken, buyToken, useMaxAmount)
                }, 200)
            })
            .catch(err => {
                console.error('Error processing AI request:', err)
                setError(err.message || 'Failed to process request. Please try again.')
                setIsLoading(false)
            })
    }

    const handleParseRequest = () => {
        if (isLoading || !prompt.trim()) return
        handleSubmit({
            preventDefault: () => {
                /* Mock preventDefault */
            },
            type: 'submit',
        } as FormEvent<HTMLFormElement>)
    }

    useEffect(() => {
        if (success) {
            const timeoutId = setTimeout(() => setSuccess(null), 5000)
            return () => clearTimeout(timeoutId)
        }
    }, [success])

    // Helper function to compare gas tokens priority (similar to SelectTokenModal)
    const compareGasTokens = (a: any, b: any): number => {
        // Native tokens (like ETH) should come first
        if (a.native && !b.native) return -1
        if (!a.native && b.native) return 1

        // Then wrapped versions (like WETH)
        if (a.wrapped && !b.wrapped) return -1
        if (!a.wrapped && b.wrapped) return 1

        return 0
    }

    // Function to generate suggestions based on current input
    const generateSuggestions = (input: string) => {
        if (!input.trim()) {
            setSuggestions([])
            setShowSuggestions(false)
            setPreviewText(null)
            setInlinePreviewText('')
            return
        }

        const lastWord = input.split(/\s+/).pop() || ''

        // Check if user is searching for a chain name
        const matchingChainIds: number[] = []

        // Use Fuse for case-insensitive chain name matching
        const chainFuse = new Fuse(Object.entries(availableNetworks), {
            keys: [['1', 'name']], // path to 'name' in [chainIdStr, network] entries
            threshold: 0.4, // A lower threshold makes matching more strict
            ignoreLocation: true, // Search the entire string
            isCaseSensitive: false, // Case insensitive matching
        })

        const chainResults = chainFuse.search(lastWord)
        chainResults.forEach(result => {
            matchingChainIds.push(parseInt(result.item[0]))
        })

        // Configure Fuse for token searching
        const tokenFuse = new Fuse(tokens, {
            keys: [
                { name: 'symbol', weight: 2 }, // Give more weight to symbol matches
                { name: 'name', weight: 1 },
            ],
            threshold: 0.4,
            ignoreLocation: true,
            isCaseSensitive: false,
            useExtendedSearch: true,
        })

        // Search tokens with Fuse
        const tokenResults = tokenFuse.search(lastWord)

        // Extract tokens from search results
        let matchedTokens = tokenResults.map(result => result.item)

        // Also include tokens on matching chains if not already included
        if (matchingChainIds.length > 0) {
            const chainMatchedTokens = tokens.filter(
                token =>
                    matchingChainIds.includes(token.chainId) &&
                    !matchedTokens.some(t => t.symbol === token.symbol && t.chainId === token.chainId)
            )

            matchedTokens = [...matchedTokens, ...chainMatchedTokens]
        }

        // Get top tokens based on ranking criteria
        const tokenSuggestions = matchedTokens
            .slice(0, 30) // Get more tokens initially for better sorting
            .sort((a, b) => {
                // First prioritize exact symbol matches
                const aExactSymbolMatch = a.symbol.toLowerCase() === lastWord.toLowerCase()
                const bExactSymbolMatch = b.symbol.toLowerCase() === lastWord.toLowerCase()

                if (aExactSymbolMatch && !bExactSymbolMatch) return -1
                if (!aExactSymbolMatch && bExactSymbolMatch) return 1

                // Then prioritize symbol matches at start
                const aStartsWithMatch = a.symbol.toLowerCase().startsWith(lastWord.toLowerCase())
                const bStartsWithMatch = b.symbol.toLowerCase().startsWith(lastWord.toLowerCase())

                if (aStartsWithMatch && !bStartsWithMatch) return -1
                if (!aStartsWithMatch && bStartsWithMatch) return 1

                // Chain matches get higher priority
                const aChainMatch = matchingChainIds.includes(a.chainId)
                const bChainMatch = matchingChainIds.includes(b.chainId)

                if (aChainMatch && !bChainMatch) return -1
                if (!aChainMatch && bChainMatch) return 1

                // Then gas tokens
                const gasTokenComparison = compareGasTokens(a, b)
                if (gasTokenComparison !== 0) return gasTokenComparison

                // Then sort by user balance
                const aBalance = a.userBalanceInUSD ?? 0
                const bBalance = b.userBalanceInUSD ?? 0
                if (aBalance && bBalance) {
                    return bBalance - aBalance
                } else if (aBalance) {
                    return -1
                } else if (bBalance) {
                    return 1
                }

                // Finally sort by market cap
                const aCap = a.market_cap ?? -Infinity
                const bCap = b.market_cap ?? -Infinity
                return bCap - aCap
            })
            .slice(0, 9)
            .map(token => ({
                type: 'token' as const,
                value: token.symbol,
                display: `${token.symbol} - ${token.name}`,
                data: token,
                shortcutNumber: 0, // We'll set this later
                uniqueId: `token-${token.symbol}-${token.chainId}`, // Add a unique ID to prevent duplicates
                // Flag if this token is shown due to chain match
                isChainMatch: matchingChainIds.includes(token.chainId),
            }))

        // Assign sequential shortcut numbers
        // Use a Set to track unique tokens by symbol+chain to avoid duplicate shortcuts
        const seenTokens = new Set<string>()
        let shortcutCounter = 1

        tokenSuggestions.forEach(suggestion => {
            // Create a unique identifier for the token
            const uniqueKey = `${suggestion.data.symbol}-${suggestion.data.chainId}`

            // If we haven't seen this token before, assign a shortcut
            if (!seenTokens.has(uniqueKey)) {
                suggestion.shortcutNumber = shortcutCounter++
                seenTokens.add(uniqueKey)
            } else {
                // For duplicates, don't assign a shortcut
                suggestion.shortcutNumber = 0
            }
        })

        setSuggestions(tokenSuggestions)
        setShowSuggestions(tokenSuggestions.length > 0)

        // Set active index to 0 (first item) by default when suggestions appear
        if (tokenSuggestions.length > 0) {
            setActiveSuggestionIndex(0)
            const previewText = getPreviewTextForSuggestion(tokenSuggestions[0])
            updatePreviewText(tokenSuggestions[0])

            // Generate inline preview text
            const words = input.split(/\s+/)
            const lastIndex = words.length - 1
            const lastWordInInput = words[lastIndex]

            // Get the completion part (what would be added)
            if (previewText.toLowerCase().startsWith(lastWordInInput.toLowerCase())) {
                const completionPart = previewText.substring(lastWordInInput.length)
                setInlinePreviewText(completionPart)
            } else {
                setInlinePreviewText('')
            }
        } else {
            setActiveSuggestionIndex(-1)
            setPreviewText(null)
            setInlinePreviewText('')
        }
    }

    // Function to generate balance-based suggestions
    const generateBalanceSuggestions = () => {
        // Filter for tokens with positive user balance
        const tokensWithBalance = tokens.filter(
            token => token.userBalanceInUSD !== undefined && token.userBalanceInUSD > 0
        )

        // Sort tokens by priority
        const sortedTokens = tokensWithBalance.sort((a, b) => {
            // First prioritize gas tokens
            const gasTokenComparison = compareGasTokens(a, b)
            if (gasTokenComparison !== 0) return gasTokenComparison

            // Then sort by user balance in USD (highest to lowest)
            const aBalance = a.userBalanceInUSD ?? 0
            const bBalance = b.userBalanceInUSD ?? 0
            if (aBalance && bBalance) {
                return bBalance - aBalance
            } else if (aBalance) {
                return -1
            } else if (bBalance) {
                return 1
            }

            // Finally sort by market cap
            const aCap = a.market_cap ?? -Infinity
            const bCap = b.market_cap ?? -Infinity
            return bCap - aCap
        })

        const topTokens = sortedTokens.slice(0, 9)

        // Format balance for display
        const balanceSuggestions = topTokens.map((token, index) => {
            // Format USD value with appropriate precision
            const usdValue = token.userBalanceInUSD ?? 0
            const formattedUSD =
                usdValue < 0.01 ? '<$0.01' : usdValue < 1 ? `$${usdValue.toFixed(4)}` : `$${usdValue.toFixed(2)}`

            return {
                type: 'token' as const,
                value: token.symbol,
                display: `${token.symbol} - ${formattedUSD}`,
                data: token,
                shortcutNumber: index < 9 ? index + 1 : 0, // Assign shortcuts 1-9
                uniqueId: `balance-${token.symbol}-${token.chainId}`,
                isChainMatch: false,
            }
        })

        setSuggestions(balanceSuggestions)
        setShowSuggestions(balanceSuggestions.length > 0)

        // Set active index to 0 (first item) by default when suggestions appear
        if (balanceSuggestions.length > 0) {
            setActiveSuggestionIndex(0)
            updatePreviewText(balanceSuggestions[0])
        }
    }

    // Helper function to generate preview text for a suggestion
    const getPreviewTextForSuggestion = (suggestion: {
        type: 'token' | 'chain'
        value: string
        display: string
        data: any
        isChainMatch?: boolean
    }) => {
        // Always a token now, but we'll keep the type check for future flexibility
        if (suggestion.type === 'token') {
            const chainName = availableNetworks[suggestion.data.chainId]?.name || ''
            return `${suggestion.value} on ${chainName}`
        } else {
            return suggestion.value
        }
    }

    // Update preview text based on current selection
    const updatePreviewText = (
        suggestion: {
            type: 'token' | 'chain'
            value: string
            display: string
            data: any
            isChainMatch?: boolean
        } | null
    ) => {
        if (!suggestion) {
            setPreviewText(null)
            return
        }

        setPreviewText(getPreviewTextForSuggestion(suggestion))
    }

    // Function to update the inline preview text based on current selection
    const updateInlinePreview = (
        suggestion: {
            type: 'token' | 'chain'
            value: string
            display: string
            data: any
            isChainMatch?: boolean
        } | null
    ) => {
        if (!suggestion || !prompt) {
            setInlinePreviewText('')
            return
        }

        const words = prompt.split(/\s+/)
        const lastIndex = words.length - 1
        const lastWord = words[lastIndex].toLowerCase()

        const completionText = getPreviewTextForSuggestion(suggestion)

        // Always show some preview text regardless of exact match
        // For partial matches, show just what would be appended
        if (completionText.toLowerCase().startsWith(lastWord)) {
            const completionPart = completionText.substring(lastWord.length)
            setInlinePreviewText(completionPart)
        } else {
            // For other cases, show the full suggestion as preview
            setInlinePreviewText(` → ${completionText}`)
        }
    }

    // Handle input change
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newPrompt = e.target.value
        // Add to undo history if significant change
        if (Math.abs(newPrompt.length - prompt.length) > 1 || newPrompt.length === 0) {
            setUndoHistory(prev => [...prev, prompt])
        }
        setPrompt(newPrompt)
        generateSuggestions(newPrompt)
        updateCursorPosition()
    }

    const updateCursorPosition = () => {
        if (inputRef.current) {
            const input = inputRef.current
            // Get the text up to the cursor position
            const cursorIndex = input.selectionStart || 0
            const textBeforeCursor = prompt.substring(0, cursorIndex)

            // Create a temporary element to measure text width
            const tempElement = document.createElement('span')
            tempElement.style.font = window.getComputedStyle(input).font
            tempElement.style.position = 'absolute'
            tempElement.style.visibility = 'hidden'
            tempElement.textContent = textBeforeCursor
            document.body.appendChild(tempElement)

            // Get the position
            const inputRect = input.getBoundingClientRect()
            const textWidth = tempElement.getBoundingClientRect().width

            // Clean up
            document.body.removeChild(tempElement)

            // Calculate cursor position relative to the input field
            const cursorLeft = Math.min(textWidth, inputRect.width - 20)

            // Set the cursor position state
            setCursorPosition({
                top: inputRect.height, // This will be used with a relative position to input
                left: cursorLeft + 24, // Add left padding offset (pl-3 = 12px + icon width + margin)
                inputWidth: inputRect.width,
            })
        }
    }

    // Helper function to get text width for proper alignment
    const getTextWidth = (text: string, inputElement: HTMLInputElement | null): number => {
        if (!inputElement) return 0

        // Quick optimization for empty text
        if (!text) return 0

        const canvas = document.createElement('canvas')
        const context = canvas.getContext('2d')
        if (!context) return 0

        // Get the computed style of the input element
        const computedStyle = window.getComputedStyle(inputElement)
        const font =
            computedStyle.font || `${computedStyle.fontWeight} ${computedStyle.fontSize} ${computedStyle.fontFamily}`

        context.font = font
        const metrics = context.measureText(text)

        // Add an adjustment factor for better alignment
        // This value may need fine-tuning based on the specific font
        return Math.floor(metrics.width) + 2
    }

    // Handle keyboard navigation
    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        // Handle keyboard shortcuts for suggestions (Cmd/Ctrl + number)
        if ((e.metaKey || e.ctrlKey) && !isNaN(parseInt(e.key)) && parseInt(e.key) >= 1 && parseInt(e.key) <= 9) {
            e.preventDefault()
            const index = parseInt(e.key) - 1
            if (showSuggestions && index < suggestions.length) {
                selectSuggestion(suggestions[index])
            }
            return
        }

        // Handle Cmd+Enter to parse
        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault()
            handleParseRequest()
            return
        }

        // Handle undo (Cmd/Ctrl + Z)
        if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
            e.preventDefault()
            if (undoHistory.length > 0) {
                const prevPrompt = undoHistory[undoHistory.length - 1]
                setPrompt(prevPrompt)
                setUndoHistory(prev => prev.slice(0, -1))
                generateSuggestions(prevPrompt)
            }
            return
        }

        if (!showSuggestions) return

        updateCursorPosition()

        // Handle arrow down
        if (e.key === 'ArrowDown') {
            e.preventDefault()
            // Calculate the new index
            const newIndex =
                activeSuggestionIndex < suggestions.length - 1 ? activeSuggestionIndex + 1 : activeSuggestionIndex

            // Set the new active suggestion index
            setActiveSuggestionIndex(newIndex)

            // Update preview text and ensure the selected item is visible
            if (newIndex >= 0 && newIndex < suggestions.length) {
                updatePreviewText(suggestions[newIndex])
                updateInlinePreview(suggestions[newIndex])

                // Ensure the selected item is scrolled into view
                setTimeout(() => {
                    if (suggestionsRef.current && suggestionsRef.current.children.length > 0) {
                        const suggestionsContainer = suggestionsRef.current
                        const selectedElement = suggestionsContainer.children[0].children[newIndex] as HTMLElement

                        if (selectedElement) {
                            // Get positions
                            const containerTop = suggestionsContainer.scrollTop
                            const containerBottom = containerTop + suggestionsContainer.clientHeight
                            const elementTop = selectedElement.offsetTop
                            const elementBottom = elementTop + selectedElement.clientHeight

                            // Check if element is outside view
                            if (elementBottom > containerBottom) {
                                // Element is below visible area - scroll down
                                suggestionsContainer.scrollTop = elementBottom - suggestionsContainer.clientHeight
                            }
                        }
                    }
                }, 0)
            }
        }
        // Handle arrow up
        else if (e.key === 'ArrowUp') {
            e.preventDefault()
            // Calculate the new index
            const newIndex = activeSuggestionIndex > 0 ? activeSuggestionIndex - 1 : 0

            // Set the new active suggestion index
            setActiveSuggestionIndex(newIndex)

            // Update preview text and ensure the selected item is visible
            if (newIndex >= 0 && newIndex < suggestions.length) {
                updatePreviewText(suggestions[newIndex])
                updateInlinePreview(suggestions[newIndex])

                // Ensure the selected item is scrolled into view
                setTimeout(() => {
                    if (suggestionsRef.current && suggestionsRef.current.children.length > 0) {
                        const suggestionsContainer = suggestionsRef.current
                        const selectedElement = suggestionsContainer.children[0].children[newIndex] as HTMLElement

                        if (selectedElement) {
                            // Get positions
                            const containerTop = suggestionsContainer.scrollTop
                            const elementTop = selectedElement.offsetTop

                            // Check if element is outside view
                            if (elementTop < containerTop) {
                                // Element is above visible area - scroll up
                                suggestionsContainer.scrollTop = elementTop
                            }
                        }
                    }
                }, 0)
            }
        }
        // Handle tab for selection - must prevent default and stop propagation
        else if (e.key === 'Tab') {
            if (showSuggestions && suggestions.length > 0) {
                e.preventDefault()
                e.stopPropagation()
                // Add current state to undo history before making the change
                setUndoHistory(prev => [...prev, prompt])
                // If nothing is selected, select the first option
                const indexToUse = activeSuggestionIndex >= 0 ? activeSuggestionIndex : 0
                if (indexToUse < suggestions.length) {
                    selectSuggestion(suggestions[indexToUse])
                }
            }
            return
        }
        // Handle enter for selection (only when suggestions are shown)
        else if (e.key === 'Enter') {
            if (showSuggestions && suggestions.length > 0) {
                e.preventDefault()
                const indexToUse = activeSuggestionIndex >= 0 ? activeSuggestionIndex : 0
                selectSuggestion(suggestions[indexToUse])
            } else {
                // When no suggestions are shown, Enter submits the form
                e.preventDefault()
                handleParseRequest()
            }
        }
        // Close suggestions on escape
        else if (e.key === 'Escape') {
            e.preventDefault()
            setShowSuggestions(false)
            setPreviewText(null)
            setInlinePreviewText('')
        }
    }

    // Function to handle suggestion click or selection
    const selectSuggestion = (suggestion: {
        type: 'token' | 'chain'
        value: string
        display: string
        data: any
        isChainMatch?: boolean
    }) => {
        // Replace the last word with the selected suggestion
        const words = prompt.split(/\s+/)
        const lastIndex = words.length - 1

        // Format the replacement based on suggestion type
        const replacementText = getPreviewTextForSuggestion(suggestion)

        words[lastIndex] = replacementText
        const newPrompt = words.join(' ')

        setPrompt(newPrompt)

        // Immediately close suggestions and clear preview
        setShowSuggestions(false)
        setPreviewText(null)
        setInlinePreviewText('')
        setActiveSuggestionIndex(-1) // Reset active suggestion index
        setSuggestions([]) // Clear suggestions array to prevent further clicks

        // Focus back on input
        if (inputRef.current) {
            inputRef.current.focus()
        }
    }

    // On click inside input, update cursor position
    const handleInputClick = () => {
        updateCursorPosition()
    }

    // Handle suggestion mouse enter
    const handleSuggestionMouseEnter = (index: number) => {
        setHoveredIndex(index)
        if (index >= 0 && index < suggestions.length) {
            const suggestion = suggestions[index]
            updatePreviewText(suggestion)
            updateInlinePreview(suggestion)

            // Also update active suggestion index to visually highlight the row
            setActiveSuggestionIndex(index)
        }
    }

    // Handle suggestion mouse leave
    const handleSuggestionMouseLeave = () => {
        setHoveredIndex(-1)
        // We don't reset the active suggestion index here to maintain keyboard nav state

        // Restore preview for the active item if there is one
        if (activeSuggestionIndex >= 0 && activeSuggestionIndex < suggestions.length) {
            updatePreviewText(suggestions[activeSuggestionIndex])
            updateInlinePreview(suggestions[activeSuggestionIndex])
        }
    }

    // Setup event listeners for focus management and outside clicks
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            // Close suggestions if click outside of input and suggestions
            if (
                inputRef.current &&
                !inputRef.current.contains(event.target as Node) &&
                suggestionsRef.current &&
                !suggestionsRef.current.contains(event.target as Node)
            ) {
                setShowSuggestions(false)
                setPreviewText(null)
                setInlinePreviewText('')
                setActiveSuggestionIndex(-1)
            }
        }

        // Add event listeners
        document.addEventListener('mousedown', handleClickOutside)

        return () => {
            // Clean up on unmount
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    // Handle input focus
    const handleInputFocus = () => {
        // When input is focused and empty, show balance-based suggestions
        if (!prompt.trim()) {
            generateBalanceSuggestions()
        }
        // Otherwise, regenerate suggestions based on current input
        else if (prompt.trim()) {
            generateSuggestions(prompt)
        }
    }

    // Handle input blur
    const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        // Don't hide suggestions if we're clicking on a suggestion
        if (suggestionsRef.current && suggestionsRef.current.contains(e.relatedTarget as Node)) {
            return
        }
    }

    // Ensure active suggestion is in view when navigating
    useEffect(() => {
        if (activeSuggestionIndex >= 0 && suggestionsRef.current && suggestionsRef.current.children.length > 0) {
            const suggestionsContainer = suggestionsRef.current
            const selectedElement = suggestionsContainer.children[0].children[activeSuggestionIndex] as HTMLElement

            if (selectedElement) {
                // Get positions
                const containerTop = suggestionsContainer.scrollTop
                const containerBottom = containerTop + suggestionsContainer.clientHeight
                const elementTop = selectedElement.offsetTop
                const elementBottom = elementTop + selectedElement.clientHeight

                // Check if element is outside view
                if (elementTop < containerTop) {
                    // Element is above visible area
                    suggestionsContainer.scrollTop = elementTop
                } else if (elementBottom > containerBottom) {
                    // Element is below visible area
                    suggestionsContainer.scrollTop = elementBottom - suggestionsContainer.clientHeight
                }
            }
        }
    }, [activeSuggestionIndex])

    // Add fade-in animation for status messages and hide scrollbars
    useEffect(() => {
        // Create style element for fade-in animation and hidden scrollbars
        const style = document.createElement('style')
        style.textContent = `
            @keyframes fadeIn {
                from { opacity: 0; transform: translateY(-5px); }
                to { opacity: 1; transform: translateY(0); }
            }
            .animate-fadeIn {
                animation: fadeIn 0.3s ease-in-out;
            }
            .suggestion-dropdown::-webkit-scrollbar {
                display: none;
            }
        `
        document.head.appendChild(style)

        // Cleanup function to remove style when component unmounts
        return () => {
            document.head.removeChild(style)
        }
    }, [])

    const handleSuccessfulResponse = (
        foundSell: boolean,
        foundBuy: boolean,
        sellToken?: any,
        buyToken?: any,
        useMaxAmount?: boolean
    ) => {
        // Update loading state first
        setIsLoading(false)

        // Process results
        if (foundSell || foundBuy) {
            // Handle percentage and max amount cases
            if (setMax) {
                if (useMaxAmount) {
                    // Regular max amount case
                    // Triggering setMax function (100%)
                    setMax() // Using full amount (100%)
                } else if (sellToken?.isPercentage && typeof sellToken.amount === 'string') {
                    // Percentage amount case
                    const percentageValue = parseFloat(sellToken.amount)
                    if (!isNaN(percentageValue) && percentageValue > 0 && percentageValue <= 100) {
                        // Triggering setMax function with percentage
                        setMax(percentageValue) // Pass the percentage amount
                    }
                }
            }

            // Clear the prompt on success
            setPrompt('')

            // Generate appropriate success message
            let successMessage: string

            if (foundSell && foundBuy) {
                // Get chain names for both tokens
                const sellChainName = sellToken?.chainId ? getChainNameFromChainId(sellToken.chainId) : ''
                const buyChainName = buyToken?.chainId ? getChainNameFromChainId(buyToken.chainId) : ''

                // Format success message with chain information - capitalize first letter only
                successMessage = `Configured swap from ${sellChainName ? sellChainName.charAt(0).toUpperCase() + sellChainName.slice(1) + ' ' : ''}${sellToken?.symbol} to ${buyChainName ? buyChainName.charAt(0).toUpperCase() + buyChainName.slice(1) + ' ' : ''}${buyToken?.symbol}`

                // Trigger quote refetch if we found both tokens
                refetchQuote()
            } else if (foundSell) {
                const sellChainName = sellToken?.chainId ? getChainNameFromChainId(sellToken.chainId) : ''
                successMessage = `Set ${sellChainName ? sellChainName.charAt(0).toUpperCase() + sellChainName.slice(1) + ' ' : ''}${sellToken?.symbol} as the source token`
            } else {
                const buyChainName = buyToken?.chainId ? getChainNameFromChainId(buyToken.chainId) : ''
                successMessage = `Set ${buyChainName ? buyChainName.charAt(0).toUpperCase() + buyChainName.slice(1) + ' ' : ''}${buyToken?.symbol} as the destination token`
            }

            // Set the success message with proper type safety
            setSuccess(successMessage)

            // Clear success message after a while
            setTimeout(() => {
                setSuccess(null)
            }, 3000)
        } else {
            setError('Could not find the specified tokens. Please try rephrasing.')
        }
    }

    return (
        <div
            className={`relative mx-auto mb-4 w-[464px] max-w-[calc(100%-24px)] rounded-xl border border-mach-modal-border bg-mach-modal-background ${className}`}
        >
            {/* Loading background that fills the entire container */}
            {isLoading && (
                <div
                    className='absolute bottom-0 left-0 top-0 z-0 bg-blue-500/30'
                    style={{
                        width: `${requestProgress}%`,
                        transition: 'width 100ms linear',
                        borderTopLeftRadius: '0.75rem',
                        borderBottomLeftRadius: '0.75rem',
                        borderTopRightRadius: requestProgress >= 99 ? '0.75rem' : '0',
                        borderBottomRightRadius: requestProgress >= 99 ? '0.75rem' : '0',
                    }}
                ></div>
            )}

            <div className='relative z-10 p-3'>
                <form onSubmit={handleSubmit} className='flex items-center gap-2'>
                    <div className='bg-mach-input-background relative flex h-10 flex-1 items-center overflow-hidden rounded-lg pl-3 pr-2'>
                        <span className='text-mach-accent relative z-10 mr-2'>
                            <Sparkles
                                size={16}
                                className={`${isLoading ? 'animate-pulse text-blue-400' : 'text-mach-accent'}`}
                            />
                        </span>
                        <div className='relative z-10 flex h-full flex-1 items-center'>
                            <input
                                type='text'
                                value={prompt}
                                onChange={handleInputChange}
                                onKeyDown={handleKeyDown}
                                onFocus={handleInputFocus}
                                onBlur={handleInputBlur}
                                onClick={handleInputClick}
                                placeholder="Try '10 USDT ARB to USDC BASE'"
                                className={`h-full w-full bg-transparent text-sm ${isLoading ? 'text-gray-400' : 'text-white'} pr-2 placeholder-gray-400 outline-none transition-colors duration-200`}
                                disabled={isLoading}
                                ref={inputRef}
                            />
                            {/* Inline preview text with improved vertical alignment */}
                            {inlinePreviewText && (
                                <div
                                    className='pointer-events-none absolute z-10 flex h-full items-center'
                                    style={{
                                        left: `${Math.min(getTextWidth(prompt, inputRef.current), (inputRef.current?.clientWidth || 0) - 40)}px`,
                                        maxWidth: inputRef.current
                                            ? `calc(100% - ${getTextWidth(prompt, inputRef.current)}px - 16px)`
                                            : '70%',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    <span className='overflow-hidden text-ellipsis text-sm text-gray-400'>
                                        {inlinePreviewText}
                                    </span>
                                    <span className='bg-mach-accent/20 border-mach-accent/30 text-mach-accent ml-1.5 flex flex-shrink-0 items-center rounded-md border px-1.5 py-0.5 text-xs'>
                                        <span className='mr-0.5'>⇥</span>Tab
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Preview pill */}
                        {previewText && showSuggestions && (
                            <div className='bg-mach-accent/20 border-mach-accent/40 absolute bottom-[-34px] left-[46px] z-40 flex items-center whitespace-nowrap rounded-full border px-3 py-1 text-xs text-white shadow-md'>
                                <span className='text-mach-accent mr-1.5 font-medium'>Tab →</span>
                                {previewText}
                            </div>
                        )}
                    </div>

                    <button
                        className={`flex items-center justify-center rounded-md px-4 py-2 ${
                            isLoading || !prompt.trim() ? 'cursor-not-allowed opacity-50' : 'hover:bg-mach-accent/20'
                        }`}
                        onClick={handleParseRequest}
                        disabled={isLoading || !prompt.trim()}
                    >
                        {isLoading ? (
                            <div className='flex items-center'>
                                <Loader2 className='mr-1 h-4 w-4 animate-spin text-blue-400' />
                                <span className='text-xs text-blue-400'>
                                    {loadingStage === 'processing'
                                        ? 'Processing'
                                        : loadingStage === 'analyzing'
                                          ? 'Analyzing'
                                          : 'Almost done'}
                                </span>
                            </div>
                        ) : (
                            <span className='bg-mach-accent/10 border-mach-accent/30 flex items-center rounded border px-1.5 py-0.5 text-xs'>
                                {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+↵
                            </span>
                        )}
                    </button>
                </form>

                {/* Autocomplete suggestions */}
                {showSuggestions && suggestions.length > 0 && (
                    <div
                        ref={suggestionsRef}
                        style={{
                            position: 'absolute',
                            top: `${cursorPosition.top + 36}px`, // Position below the input (input height + some padding)
                            left: `${cursorPosition.left}px`,
                            maxWidth: `${Math.min(300, cursorPosition.inputWidth)}px`,
                            maxHeight: '230px', // Approximately 5 items
                            zIndex: 50,
                            msOverflowStyle: 'auto' /* IE and Edge */,
                            scrollbarWidth: 'thin' /* Firefox */,
                            WebkitOverflowScrolling: 'touch',
                        }}
                        className='suggestion-dropdown no-scrollbar w-auto overflow-auto rounded-md border border-mach-modal-border bg-mach-modal-background shadow-lg'
                        onScroll={e => e.stopPropagation()}
                    >
                        <div className='py-1'>
                            {suggestions.map((suggestion, index) => (
                                <div
                                    key={suggestion.uniqueId}
                                    className={`flex cursor-pointer items-center px-4 py-2 ${
                                        index === activeSuggestionIndex
                                            ? 'bg-highlight-text/20'
                                            : index === hoveredIndex
                                              ? 'bg-highlight-text/15'
                                              : 'hover:bg-highlight-text/10'
                                    }`}
                                    onMouseDown={e => {
                                        // Using mousedown instead of click to ensure this happens before blur events
                                        e.preventDefault() // Prevent default to avoid losing focus
                                        e.stopPropagation() // Stop event from bubbling
                                        setShowSuggestions(false) // Immediately close suggestions
                                        selectSuggestion(suggestion) // Then handle the selection
                                    }}
                                    onMouseEnter={() => handleSuggestionMouseEnter(index)}
                                    onMouseLeave={handleSuggestionMouseLeave}
                                >
                                    {suggestion.type === 'token' && (
                                        <div className='mr-2 overflow-hidden'>
                                            <ChainToken
                                                tokenLogo={suggestion.data.logoURI}
                                                networkIcon={`/assets/network/${getChainNameFromChainId(suggestion.data.chainId)}.png`}
                                                tokenIconSize={24}
                                                chainIconSize={12}
                                            />
                                        </div>
                                    )}
                                    {suggestion.type === 'chain' && (
                                        <div className='mr-2 overflow-hidden'>
                                            <img
                                                src={suggestion.data.network.icon}
                                                alt={suggestion.data.network.name}
                                                className='h-6 w-6 rounded-md object-cover'
                                            />
                                        </div>
                                    )}
                                    <div className='flex flex-grow flex-col'>
                                        <span className='flex items-center justify-between text-sm text-white'>
                                            <span className='max-w-[160px] overflow-hidden text-ellipsis whitespace-nowrap'>
                                                {suggestion.display}
                                            </span>
                                            <span className='ml-2 flex flex-shrink-0 items-center rounded bg-gray-800 px-1 text-xs text-gray-500'>
                                                {suggestion.shortcutNumber > 0
                                                    ? `${navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+${suggestion.shortcutNumber}`
                                                    : ''}
                                            </span>
                                        </span>
                                        <span className='max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap text-xs text-gray-400'>
                                            {suggestion.type === 'token'
                                                ? availableNetworks[suggestion.data.chainId]?.name
                                                : 'Chain'}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Status Messages with enhanced styling */}
                {error && (
                    <div className='mt-2 flex animate-fadeIn items-center gap-1.5 rounded-md border border-red-500/20 bg-red-500/10 p-2 text-xs text-red-400'>
                        <AlertCircle size={14} className='flex-shrink-0' />
                        <p>{error}</p>
                    </div>
                )}

                {success && (
                    <div className='mt-2 flex animate-fadeIn items-center gap-1.5 rounded-md border border-green-500/20 bg-green-500/10 p-2 text-xs text-green-400'>
                        <CheckCircle size={14} className='flex-shrink-0' />
                        <p>{success}</p>
                    </div>
                )}
            </div>
        </div>
    )
}

export default AIInputBox
